import { AUTH } from '@libs/network-utils';
import { selectCurrentPageId, selectScenarioId, useAppSelector } from '@libs/store';
import { TUseCache, TWidget } from '@libs/types';
import { useCallback, useEffect } from 'react';

const simpleHash = (str: string | null | undefined): string => {
    if (!str) {
        return 'default';
    }
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash |= 0; // Convert to 32bit integer
    }
    return hash.toString();
};

const CACHE_PREFIX = 'ps3_';

let memoizedPrefix: string | null = null;

const getCachePrefix = () => {
    if (memoizedPrefix) {
        return memoizedPrefix;
    }

    const authLocalStorage = localStorage?.getItem(AUTH);
    if (authLocalStorage) {
        try {
            // If value is a valid JSON, it's an auth object.
            // If not, it's a deviceId string, and parsing will fail.
            const parsedAuth = JSON.parse(authLocalStorage);
            memoizedPrefix = CACHE_PREFIX + simpleHash(parsedAuth?.refresh);
            return memoizedPrefix;
        } catch (e) {
            // Suppress: value is not a valid JSON auth object (likely a deviceId)
            console.error(e);
        }
    }
    return CACHE_PREFIX;
};

/**
 * Resets the memoized cache prefix so the next `getCachePrefix()` call
 * recomputes it from the current auth state. Call after clearing caches on
 * logout when the page is not reloaded.
 */
export const resetCachePrefix = (): void => {
    memoizedPrefix = null;
};

/**
 * Deletes every Cache Storage entry created by the app (all caches whose name
 * starts with the `ps3_` prefix), regardless of the auth-token hash. Intended
 * to be called imperatively on logout / closeFramework.
 */
export const clearAllCaches = async (): Promise<void> => {
    if (typeof caches === 'undefined') {
        return;
    }
    try {
        const keys = await caches.keys();
        if (!Array.isArray(keys)) {
            return;
        }
        await Promise.all(keys.filter((k) => k.startsWith(CACHE_PREFIX)).map((k) => caches.delete(k)));
        resetCachePrefix();
    } catch (e) {
        console.error('Failed to clear caches:', e);
    }
};

export const useCache = <TD>(widget: TWidget<TD>): TUseCache<TD> => {
    const scenarioId = useAppSelector(selectScenarioId);
    const currentPageId = useAppSelector(selectCurrentPageId);

    const key = widget.internalId || widget.uid;

    const cacheName = `${getCachePrefix()}_${scenarioId}_${currentPageId}`;
    const expiryTime = 30;

    useEffect(() => {
        caches.keys().then((keys) => {
            if (Array.isArray(keys)) {
                keys.filter((key) => key.startsWith(getCachePrefix()))
                    .filter((k) => k !== cacheName)
                    .forEach((k) => {
                        caches.delete(k);
                    });
            }
        });
    }, [cacheName]);

    return {
        putCache: useCallback(
            async (data: TD) => {
                const cache = await caches.open(cacheName);
                const expiry = Date.now() + expiryTime * 60 * 1000;

                const response = new Response(JSON.stringify({ data, expiry }), {
                    headers: { 'Content-Type': 'application/json', 'Cache-Control': 'max-age=3600' },
                });

                await cache.put(key, response);
            },
            [cacheName, key],
        ),
        getCache: useCallback(async () => {
            const cache = await caches.open(cacheName);
            const response = await cache.match(key);

            if (!response) return null;

            const cachedData = await response.json();
            const now = Date.now();

            if (now < cachedData.expiry) {
                return cachedData.data;
            } else {
                // Если время истекло, удаляем элемент
                await cache.delete(key);
                return null;
            }
        }, [cacheName, key]),
        clearCache: useCallback(async () => {
            const cache = await caches.open(cacheName);
            await cache.delete(key);
        }, [cacheName, key]),
    };
};
